import React, { Context, createContext, PureComponent, ReactNode } from 'react';
import { userCountry } from '../../configs/env';
import { withErrorBoundary } from '../../error-handling/withErrorBoundary';
import { getUserLocationCenter } from '../../helper/LocationHelper';
import { Maybe } from '../../types';
import {
  selectIsStaticContentLoaded,
  selectVehicleCenters
} from '../Typo3Provider/selectors';
import { VehicleCenter } from '../Typo3Provider/types';
import { withTypo3, WithTypo3Props } from '../Typo3Provider/withTypo3';

interface NearestVehicleCenterProviderProps {
  children: ReactNode;
}

export interface NearestVehicleCenterContextValue {
  vehicleCenter: Maybe<VehicleCenter>;
}

type NearestVehicleCenterContextType = Context<
  NearestVehicleCenterContextValue
>;

const defaultContextValue: NearestVehicleCenterContextValue = {
  vehicleCenter: null
};

export const NearestVehicleCenterContext: NearestVehicleCenterContextType = createContext(
  defaultContextValue
);

interface NearestVehicleCenterProviderState {
  contextValue: NearestVehicleCenterContextValue;
  vehicleCenters: VehicleCenter[];
}

class NearestVehicleCenterProvider extends PureComponent<
  NearestVehicleCenterProviderProps & WithTypo3Props,
  NearestVehicleCenterProviderState
> {
  public static getDerivedStateFromProps(
    { typo3 }: NearestVehicleCenterProviderProps & WithTypo3Props,
    prevState: NearestVehicleCenterProviderState
  ): Partial<NearestVehicleCenterProviderState> | null {
    if (!selectIsStaticContentLoaded(typo3)) {
      return null;
    }
    const vehicleCenters = selectVehicleCenters(typo3);
    if (vehicleCenters !== prevState.vehicleCenters) {
      return {
        vehicleCenters,
        contextValue: {
          vehicleCenter:
            getUserLocationCenter(vehicleCenters, null, userCountry) ||
            vehicleCenters.find(({ searchValue }) => searchValue === 'GWC')
        }
      };
    }
    return null;
  }

  public state: NearestVehicleCenterProviderState = {
    contextValue: { vehicleCenter: null },
    vehicleCenters: []
  };

  public render() {
    const { children } = this.props;
    const { contextValue } = this.state;
    return (
      <NearestVehicleCenterContext.Provider value={contextValue}>
        {children}
      </NearestVehicleCenterContext.Provider>
    );
  }
}

export default withErrorBoundary(
  withTypo3(NearestVehicleCenterProvider),
  'NearestVehicleCenterProvider'
);
